export const content = {
  en: {
    nav: { platform: "Platform", modules: "Modules", packages: "Packages", industries: "Industries", intelligence: "Intelligence", demo: "Request demo", login: "Log in" },
    hero: {
      eyebrow: "Industrial Intelligence Platform",
      title: "See where your factory loses time, money and capacity.",
      text: "Opement connects planning, production, maintenance, quality, inventory, logistics and executive intelligence in a single industrial platform.",
      note: "From customer orders to shipment and beyond — in real time.",
      primary: "Request a demo",
      secondary: "Explore platform"
    },
    valueCards: [
      { title: "Loss visibility", text: "Find hidden cost, downtime and scrap across the operation." },
      { title: "End-to-end flow", text: "Connect order, planning, production, quality, warehouse and shipment." },
      { title: "Management clarity", text: "Give owners and managers the numbers that drive decisions." }
    ],
    productScreens: {
      eyebrow: "Real product screens",
      title: "See Opement in action.",
      text: "Manage planning, production, machines, work orders, quality and performance data through a single industrial platform.",
      placeholder: "Screen preview is being prepared",
      items: [
        {
          title: "Live Management Dashboard",
          text: "Monitor factory status, production performance, active work orders and critical alerts from a single screen.",
          image: "/product-screens/en/dashboard.png",
          alt: "Opement live management dashboard",
          highlights: [
            "Real-time production visibility",
            "Machine and work order status",
            "Management KPIs"
          ]
        },
        {
          title: "Machine & SCADA View",
          text: "Track machine status, connectivity, program, alarms, RPM, feed rate and part-count data coming from real machines.",
          image: "/product-screens/en/machines.png",
          alt: "Opement machine and SCADA screen",
          highlights: [
            "Real-time machine status",
            "FANUC and Edge connectivity",
            "Alarm and telemetry visibility"
          ]
        },
        {
          title: "Work Order Management",
          text: "Plan work orders, assign operators and machines, and manage production through start, pause, resume and completion steps.",
          image: "/product-screens/en/work-orders.png",
          alt: "Opement work order management screen",
          highlights: [
            "Order-to-production connection",
            "Operator workflow",
            "Production quantity tracking"
          ]
        },
        {
          title: "MES Runtime",
          text: "Calculate running, waiting and downtime for the active work order in real time while monitoring production quantity and machine counters.",
          image: "/product-screens/en/runtime.png",
          alt: "Opement MES Runtime screen",
          highlights: [
            "Running and waiting times",
            "Live production quantity",
            "Operator and machine matching"
          ]
        },
        {
          title: "OEE & Performance",
          text: "Evaluate availability, performance and quality components using real production data.",
          image: "/product-screens/en/oee.png",
          alt: "Opement OEE performance screen",
          highlights: [
            "Availability",
            "Performance",
            "Quality and OEE"
          ]
        },
        {
          title: "Quality Management",
          text: "Connect in-process and final quality controls to work orders and manage passed, failed and pending inspections.",
          image: "/product-screens/en/quality.png",
          alt: "Opement quality management screen",
          highlights: [
            "In-process quality",
            "Final quality control",
            "Nonconformity tracking"
          ]
        }
      ]
    },
    modules: {
      eyebrow: "Platform modules",
      title: "One platform for the full factory process.",
      text: "Opement is designed to connect every critical workflow from commercial demand to production execution, service and returns.",
      groups: [
        { title: "Orders & Planning", items: ["Customer orders", "MRP planning", "Work orders", "BOM & products", "Capacity view", "Approval flows"] },
        { title: "Production & Machines", items: ["Production tracking", "OEE dashboards", "Machine status", "Alarms", "Predictive maintenance", "Energy monitoring"] },
        { title: "Quality & Improvement", items: ["Quality control", "Nonconformity", "5S audits", "Kaizen suggestions", "Threshold settings", "PDF reports"] },
        { title: "Inventory & Logistics", items: ["Raw material stock", "Supplier tracking", "QR traceability", "Warehouse management", "Shipment & delivery", "Returns"] },
        { title: "People & Service", items: ["HR", "Payroll", "Attendance", "Training", "Service management", "Ticket tracking"] },
        { title: "Executive Intelligence", items: ["Financial summary", "Cost visibility", "Downtime cost", "Capacity analysis", "Performance KPIs", "Custom dashboards"] }
      ]
    },
    packages: {
      eyebrow: "Flexible packages",
      title: "Start with the visibility you need. Expand as your factory grows.",
      text: "Choose the operational scope you need today. Add selected modules as your requirements grow without having to replace your entire package.",
      noPrice: "Pricing is prepared specifically for your factory scope, machine structure, selected modules and operational requirements.",
      cta: "Request a tailored offer",
      items: [
        {
          code: "essentials",
          name: "Essentials",
          subtitle: "See",
          description: "Gain real-time visibility into your machines and factory operations without replacing your existing ERP.",
          features: [
            "Management dashboard",
            "Live machine status",
            "OEE & operational visibility",
            "Machine alarms & production signals",
            "Operational reports"
          ]
        },
        {
          code: "professional",
          name: "Professional",
          subtitle: "See + Manage",
          description: "Turn factory visibility into connected production and operational management.",
          features: [
            "Everything in Essentials",
            "Work order & production management",
            "Quality & NCR management",
            "Assembly processes",
            "Products & BOM",
            "Inventory management",
            "Sales orders & purchasing",
            "HR & attendance",
            "Shift management"
          ]
        },
        {
          code: "advanced",
          name: "Advanced",
          subtitle: "See + Manage + Optimize",
          description: "Optimize production performance, maintenance, energy, cost and continuous improvement from one operational platform.",
          featured: true,
          features: [
            "Everything in Professional",
            "Maintenance management",
            "Energy monitoring",
            "Quality cost visibility",
            "Kaizen",
            "5S",
            "Lean management"
          ]
        },
        {
          code: "enterprise",
          name: "Enterprise",
          subtitle: "See + Manage + Optimize + Integrate",
          description: "Extend Opement across your organization with enterprise governance, integrations and tailored operational configuration.",
          features: [
            "Everything in Advanced",
            "Organization management",
            "Advanced user management",
            "Enterprise governance",
            "System & ERP integrations",
            "Tailored enterprise configuration",
            "Enterprise deployment options"
          ]
        }
      ]
    },

    intelligence: {
      eyebrow: "Executive intelligence",
      title: "Built around the questions factory leaders ask every day.",
      text: "Where did we lose time? Which operation created cost? Which work order is late? Which machine needs action? Which shipment is at risk? Opement turns operational signals into management answers.",
      steps: [
        { n: "01", title: "Order", text: "Start with demand, customer requirements and delivery promises." },
        { n: "02", title: "Plan", text: "Convert orders into material, capacity and production plans." },
        { n: "03", title: "Execute", text: "Run production, machines, operators, quality and maintenance." },
        { n: "04", title: "Deliver", text: "Track warehouse, shipment, service and returns in one flow." }
      ]
    },
    industries: {
      eyebrow: "Industries",
      title: "Designed for manufacturers across industries.",
      list: ["Automotive & parts", "Metal processing", "CNC machining", "Plastics & composites", "Foundry", "Machine manufacturing", "Packaging", "Food & beverage", "Agricultural machinery", "Defense & aerospace"]
    },
    demo: {
      eyebrow: "Request demo",
      title: "See how Opement can fit your operation.",
      text: "Tell us about your factory and the process you want to improve. We will prepare a focused demo path around your real operational pain points.",
      placeholders: ["Full name", "Company", "Business email", "Phone", "Industry", "Where do you lose time, money or capacity?"],
      submit: "Send request",
      quoteOption: "Request a quote",
      demoOption: "Request a demo",
      submitQuote: "Send quote request",
      submitDemo: "Send demo request",
      error: "Your request could not be sent. Please try again.",
      success: "Thank you. Your request has been received."
    },
    footer: "Smarter operations. Better results."
  },
  tr: {
    nav: { platform: "Platform", modules: "Modüller", packages: "Paketler", industries: "Sektörler", intelligence: "Zekâ", demo: "Demo talep et", login: "Giriş" },
    hero: {
      eyebrow: "Endüstriyel Zekâ Platformu",
      title: "Fabrikanızın nerede zaman, para ve kapasite kaybettiğini görün.",
      text: "Opement; planlama, üretim, bakım, kalite, stok, lojistik ve yönetici zekâsını tek bir endüstriyel platformda birleştirir.",
      note: "Müşteri siparişinden sevkiyata ve sonrasına kadar — gerçek zamanlı.",
      primary: "Demo talep et",
      secondary: "Platformu incele"
    },
    valueCards: [
      { title: "Kayıp görünürlüğü", text: "Operasyon genelindeki gizli maliyet, duruş ve fireyi görün." },
      { title: "Uçtan uca akış", text: "Sipariş, planlama, üretim, kalite, depo ve sevkiyatı bağlayın." },
      { title: "Yönetim netliği", text: "Patron ve yöneticilere karar aldıran sayıları gösterin." }
    ],
    productScreens: {
      eyebrow: "Gerçek ürün ekranları",
      title: "Opement’i çalışırken görün.",
      text: "Planlama, üretim, makineler, iş emirleri, kalite ve performans verilerini tek endüstriyel platform üzerinden yönetin.",
      placeholder: "Ekran görüntüsü hazırlanıyor",
      items: [
        {
          title: "Canlı Yönetim Paneli",
          text: "Fabrikanın anlık durumunu, üretim performansını, aktif iş emirlerini ve kritik uyarıları tek ekranda izleyin.",
          image: "/product-screens/tr/dashboard.png",
          alt: "Opement canlı yönetim paneli",
          highlights: [
            "Anlık üretim görünümü",
            "Makine ve iş emri durumu",
            "Yönetim KPI’ları"
          ]
        },
        {
          title: "Makine ve SCADA Görünümü",
          text: "Gerçek makinelerden gelen çalışma durumu, bağlantı, program, alarm, RPM, ilerleme ve parça sayısı verilerini takip edin.",
          image: "/product-screens/tr/machines.png",
          alt: "Opement makine ve SCADA ekranı",
          highlights: [
            "Gerçek zamanlı makine durumu",
            "FANUC ve Edge bağlantısı",
            "Alarm ve telemetri görünümü"
          ]
        },
        {
          title: "İş Emri Yönetimi",
          text: "İş emirlerini planlayın, operatöre ve makineye atayın; üretim sürecini başlatma, duraklatma, devam ettirme ve tamamlama adımlarıyla yönetin.",
          image: "/product-screens/tr/work-orders.png",
          alt: "Opement iş emri yönetimi ekranı",
          highlights: [
            "Siparişten üretime bağlantı",
            "Operatör çalışma akışı",
            "Üretim miktarı takibi"
          ]
        },
        {
          title: "MES Runtime",
          text: "Aktif iş emrinin çalışma, bekleme ve duruş sürelerini canlı olarak hesaplayın; üretilen miktarı ve makine sayacını izleyin.",
          image: "/product-screens/tr/runtime.png",
          alt: "Opement MES Runtime ekranı",
          highlights: [
            "Çalışma ve bekleme süreleri",
            "Canlı üretim miktarı",
            "Operatör ve makine eşleşmesi"
          ]
        },
        {
          title: "OEE ve Performans",
          text: "Kullanılabilirlik, performans ve kalite bileşenlerini gerçek üretim verileri üzerinden değerlendirin.",
          image: "/product-screens/tr/oee.png",
          alt: "Opement OEE performans ekranı",
          highlights: [
            "Kullanılabilirlik",
            "Performans",
            "Kalite ve OEE"
          ]
        },
        {
          title: "Kalite Yönetimi",
          text: "Proses içi ve final kalite kontrollerini iş emirleriyle ilişkilendirin; uygun, uygunsuz ve bekleyen kontrolleri yönetin.",
          image: "/product-screens/tr/quality.png",
          alt: "Opement kalite yönetimi ekranı",
          highlights: [
            "Proses içi kalite",
            "Final kalite kontrol",
            "Uygunsuzluk takibi"
          ]
        }
      ]
    },
    modules: {
      eyebrow: "Platform modülleri",
      title: "Tüm fabrika süreci için tek platform.",
      text: "Opement, ticari talepten üretim yürütmeye, servis ve iade süreçlerine kadar kritik iş akışlarını bağlamak için tasarlanır.",
      groups: [
        { title: "Sipariş & Planlama", items: ["Müşteri siparişleri", "MRP planlama", "İş emirleri", "BOM & ürünler", "Kapasite görünümü", "Onay akışları"] },
        { title: "Üretim & Makineler", items: ["Üretim takibi", "OEE panelleri", "Makine durumu", "Alarmlar", "Arıza tahmini", "Enerji izleme"] },
        { title: "Kalite & İyileştirme", items: ["Kalite kontrol", "Uygunsuzluk", "5S denetimleri", "Kaizen önerileri", "Eşik ayarları", "PDF raporlar"] },
        { title: "Stok & Lojistik", items: ["Hammadde stoku", "Tedarikçi takibi", "QR izlenebilirlik", "Depo yönetimi", "Sevkiyat & teslimat", "İadeler"] },
        { title: "İnsan & Servis", items: ["İK", "Bordro", "Devam takibi", "Eğitim", "Servis yönetimi", "Talep takibi"] },
        { title: "Yönetici Zekâsı", items: ["Finansal özet", "Maliyet görünürlüğü", "Duruş maliyeti", "Kapasite analizi", "Performans KPI", "Özel paneller"] }
      ]
    },
    packages: {
      eyebrow: "Esnek paketler",
      title: "İhtiyacınız olan görünürlükle başlayın. Fabrikanızla birlikte büyütün.",
      text: "Bugün ihtiyacınız olan operasyon kapsamını seçin. Gereksinimleriniz büyüdükçe tüm paketi değiştirmek zorunda kalmadan seçili ek modülleri kapsamınıza dahil edin.",
      noPrice: "Fiyatlandırma; fabrikanızın kapsamı, makine yapısı, seçilen modüller ve operasyon ihtiyaçlarına göre özel olarak hazırlanır.",
      cta: "Size özel teklif isteyin",
      items: [
        {
          code: "essentials",
          name: "Essentials",
          subtitle: "Gör",
          description: "Mevcut ERP sisteminizi değiştirmeden makinelerinizi ve fabrika operasyonlarınızı gerçek zamanlı görünür hale getirin.",
          features: [
            "Yönetim paneli",
            "Canlı makine durumu",
            "OEE & operasyon görünümü",
            "Makine alarmları & üretim sinyalleri",
            "Operasyon raporları"
          ]
        },
        {
          code: "professional",
          name: "Professional",
          subtitle: "Gör + Yönet",
          description: "Fabrika görünürlüğünü bağlantılı üretim ve operasyon yönetimine dönüştürün.",
          features: [
            "Essentials kapsamının tamamı",
            "İş emri & üretim yönetimi",
            "Kalite & NCR yönetimi",
            "Montaj süreçleri",
            "Ürün & BOM yönetimi",
            "Stok yönetimi",
            "Satış siparişleri & satın alma",
            "İK & devam takibi",
            "Vardiya yönetimi"
          ]
        },
        {
          code: "advanced",
          name: "Advanced",
          subtitle: "Gör + Yönet + Optimize Et",
          description: "Üretim performansı, bakım, enerji, maliyet ve sürekli iyileştirme süreçlerini tek operasyon platformunda optimize edin.",
          featured: true,
          features: [
            "Professional kapsamının tamamı",
            "Bakım yönetimi",
            "Enerji takibi",
            "Kalite maliyeti görünürlüğü",
            "Kaizen",
            "5S",
            "Yalın yönetim"
          ]
        },
        {
          code: "enterprise",
          name: "Enterprise",
          subtitle: "Gör + Yönet + Optimize Et + Entegre Et",
          description: "Opement'i kurumsal yönetişim, entegrasyonlar ve ihtiyaca özel operasyon yapılandırmalarıyla organizasyonunuzun geneline taşıyın.",
          features: [
            "Advanced kapsamının tamamı",
            "Organizasyon yönetimi",
            "Gelişmiş kullanıcı yönetimi",
            "Kurumsal yönetişim",
            "Sistem & ERP entegrasyonları",
            "İhtiyaca özel kurumsal yapılandırma",
            "Kurumsal kurulum seçenekleri"
          ]
        }
      ]
    },

    intelligence: {
      eyebrow: "Yönetici zekâsı",
      title: "Fabrika liderlerinin her gün sorduğu sorular etrafında tasarlandı.",
      text: "Nerede zaman kaybettik? Hangi operasyon maliyet oluşturdu? Hangi iş emri gecikti? Hangi makine aksiyon bekliyor? Hangi sevkiyat riskte? Opement operasyon sinyallerini yönetim cevaplarına dönüştürür.",
      steps: [
        { n: "01", title: "Sipariş", text: "Talep, müşteri ihtiyacı ve teslimat sözüyle başlayın." },
        { n: "02", title: "Planla", text: "Siparişleri malzeme, kapasite ve üretim planlarına dönüştürün." },
        { n: "03", title: "Uygula", text: "Üretim, makineler, operatörler, kalite ve bakımı yönetin." },
        { n: "04", title: "Teslim et", text: "Depo, sevkiyat, servis ve iadeleri tek akışta takip edin." }
      ]
    },
    industries: {
      eyebrow: "Sektörler",
      title: "Birçok üretim sektörü için tasarlandı.",
      list: ["Otomotiv & parça", "Metal işleme", "CNC imalat", "Plastik & kompozit", "Döküm", "Makine imalatı", "Ambalaj", "Gıda & içecek", "Tarım makineleri", "Savunma & havacılık"]
    },
    demo: {
      eyebrow: "Demo talebi",
      title: "Opement’in operasyonunuza nasıl uyacağını görelim.",
      text: "Fabrikanızı ve iyileştirmek istediğiniz süreci anlatın. Gerçek operasyon problemleriniz etrafında odaklı bir demo hazırlayalım.",
      placeholders: ["Ad soyad", "Firma", "Kurumsal e-posta", "Telefon", "Sektör", "Nerede zaman, para veya kapasite kaybediyorsunuz?"],
      submit: "Talebi gönder",
      quoteOption: "Teklif Talebi",
      demoOption: "Demo Talebi",
      submitQuote: "Teklif talebini gönder",
      submitDemo: "Demo talebini gönder",
      error: "Talebiniz gönderilemedi. Lütfen tekrar deneyin.",
      success: "Teşekkürler. Talebiniz alınmıştır."
    },
    footer: "Daha akıllı operasyonlar. Daha iyi sonuçlar."
  }
};
